import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { substitutionsLogApi } from '@/services/api/substitutionsLog'
import type { SubstitutionsLogEntry } from '@/services/api/substitutionsLog'
import { adminApi } from '@/services/api/admin'
import { extractErrorMessage } from '@/utils/errorHandler'
import { RefreshCw, Download, MessageCircle, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react'

const PAGE_SIZE = 50

type SortKey = 'date_asc' | 'date_desc' | 'teacher' | 'hr_status'

const HR_STATUS_LABELS: Record<string, string> = {
  unprocessed: 'Unbearbeitet',
  settled: 'Verrechnet',
  skipped: 'Nicht ausgezahlt',
}

const HR_STATUS_BADGES: Record<string, string> = {
  unprocessed: 'bg-amber-100 text-amber-700',
  settled: 'bg-emerald-100 text-emerald-700',
  skipped: 'bg-gray-200 text-gray-600',
}

const HR_PAYOUT_LABELS: Record<string, string> = {
  paid_out: 'Ausgezahlt',
  lesson_account: 'Lektionskonto',
  none: 'Keine Angabe',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const parts = iso.split('-')
  const y = Number(parts[0])
  const m = Number(parts[1] ?? '1')
  const d = Number(parts[2] ?? '1')
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatTime(t: string | null | undefined): string {
  if (!t) return ''
  return t.slice(0, 5)
}

function formatDateTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function schoolYearStart(): string {
  const now = new Date()
  const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1
  return `${year}-08-01`
}

function isoDate(d: Date): string {
  // B3: lokale Datumsteile statt toISOString() (UTC) — sonst verschieben sich
  // Presets zwischen 00:00 und 02:00 Lokalzeit (CET/CEST) um einen Tag.
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function SubstitutionsLogPage() {
  const navigate = useNavigate()

  // Filter-Zustand (Server-seitig)
  const [dateFrom, setDateFrom] = useState<string>(schoolYearStart())
  const [dateTo, setDateTo] = useState<string>('')
  const [allTime, setAllTime] = useState(false)
  const [teacherId, setTeacherId] = useState<string>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [hrStatuses, setHrStatuses] = useState<string[]>([])
  const [hrPayouts, setHrPayouts] = useState<string[]>([])
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [sort, setSort] = useState<SortKey>('date_asc')
  const [page, setPage] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0) }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const params = useMemo(() => ({
    date_from: allTime ? undefined : (dateFrom || undefined),
    date_to: allTime ? undefined : (dateTo || undefined),
    all: allTime || undefined,
    teacher_id: teacherId || undefined,
    search: search || undefined,
    hr_statuses: hrStatuses.length ? hrStatuses : undefined,
    hr_payouts: hrPayouts.length ? hrPayouts : undefined,
    unread_only: unreadOnly || undefined,
    sort,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }), [allTime, dateFrom, dateTo, teacherId, search, hrStatuses, hrPayouts, unreadOnly, sort, page])

  const listQuery = useQuery({
    queryKey: ['substitutions-log', params],
    queryFn: () => substitutionsLogApi.getList(params),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  })

  const teachersQuery = useQuery({
    queryKey: ['substitutions-log-teachers'],
    queryFn: () => adminApi.listTeachers({ limit: 500 }),
    staleTime: 5 * 60 * 1000,
  })

  const items = listQuery.data?.items ?? []
  const kpis = listQuery.data?.kpis
  const total = listQuery.data?.total ?? 0
  const hrOn = listQuery.data?.hr_module_enabled === true
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    if (listQuery.data?.hr_module_enabled !== false) return
    setHrStatuses([])
    setHrPayouts([])
    setSort(s => (s === 'hr_status' ? 'date_asc' : s))
  }, [listQuery.data?.hr_module_enabled])

  const toggleStatusFilter = (status: string) => {
    setPage(0)
    setHrStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    )
  }

  const togglePayoutFilter = (payout: string) => {
    setPage(0)
    setHrPayouts(prev =>
      prev.includes(payout) ? prev.filter(p => p !== payout) : [...prev, payout]
    )
  }

  const applyPreset = (preset: 'week' | 'month' | 'year' | 'all') => {
    setPage(0)
    if (preset === 'all') {
      setAllTime(true)
      setDateFrom('')
      setDateTo('')
      return
    }
    setAllTime(false)
    const now = new Date()
    if (preset === 'week') {
      const from = new Date(now)
      from.setDate(now.getDate() - now.getDay() + 1)
      setDateFrom(isoDate(from))
      setDateTo(isoDate(new Date(now.getTime() + 6 * 86400000)))
    } else if (preset === 'month') {
      setDateFrom(isoDate(new Date(now.getFullYear(), now.getMonth(), 1)))
      setDateTo(isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)))
    } else {
      setDateFrom(schoolYearStart())
      setDateTo('')
    }
  }

  const handleExport = async () => {
    setExporting(true)
    setExportError(null)
    try {
      await substitutionsLogApi.exportCsv({ ...params, limit: undefined, offset: undefined })
    } catch (err) {
      setExportError(extractErrorMessage(err, { loadError: 'Export fehlgeschlagen.' }))
    } finally {
      setExporting(false)
    }
  }

  const openChat = (entry: SubstitutionsLogEntry) => {
    navigate(`/teacher/chats?selected=${entry.request_id}`)
  }

  const sortIndicator = (key: SortKey) => {
    if (sort === key) return <ArrowUp className="inline w-3 h-3 ml-1 text-gray-500" />
    if (sort === 'date_desc' && key === 'date_asc') return <ArrowDown className="inline w-3 h-3 ml-1 text-gray-500" />
    return null
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stellvertretungsliste</h1>
          <p className="text-sm text-gray-500 mt-1">
            {hrOn ? 'Abgehaltene Stellvertretungen mit HR-Abgleich · Read-only' : 'Abgehaltene Stellvertretungen · Read-only'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exportiere…' : 'CSV-Export'}
          </button>
          <button
            onClick={() => listQuery.refetch()}
            disabled={listQuery.isFetching}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${listQuery.isFetching ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      {listQuery.error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {extractErrorMessage(listQuery.error as Error, { loadError: 'Liste konnte nicht geladen werden.' })}
        </div>
      )}
      {exportError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{exportError}</div>}

      {/* KPI-Karten (klickbar als Filter-Shortcut) */}
      <div className={`grid grid-cols-2 ${hrOn ? 'md:grid-cols-4' : 'md:grid-cols-1'} gap-3 mb-6`}>
        {[
          { key: 'total', label: 'Gesamt', value: kpis?.total ?? 0, color: 'text-gray-900', filter: null as string | null },
          ...(hrOn ? [
            { key: 'unprocessed', label: 'Unbearbeitet', value: kpis?.unprocessed ?? 0, color: 'text-amber-600', filter: 'unprocessed' },
            { key: 'settled', label: 'Verrechnet', value: kpis?.settled ?? 0, color: 'text-emerald-600', filter: 'settled' },
            { key: 'skipped', label: 'Nicht ausgezahlt', value: kpis?.skipped ?? 0, color: 'text-gray-500', filter: 'skipped' },
          ] : []),
        ].map(card => (
          <button
            key={card.key}
            onClick={() => card.filter && toggleStatusFilter(card.filter)}
            className={`text-left bg-white rounded-lg border p-4 transition-colors ${
              card.filter && hrStatuses.includes(card.filter)
                ? 'border-blue-400 ring-1 ring-blue-200'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-xs text-gray-500">{card.label}</div>
            <div className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</div>
          </button>
        ))}
      </div>

      {/* Filterleiste */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={allTime ? '' : dateFrom}
            disabled={allTime}
            onChange={e => { setAllTime(false); setDateFrom(e.target.value); setPage(0) }}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 disabled:bg-gray-100"
          />
          <span className="text-gray-400 text-sm">bis</span>
          <input
            type="date"
            value={allTime ? '' : dateTo}
            disabled={allTime}
            onChange={e => { setAllTime(false); setDateTo(e.target.value); setPage(0) }}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 disabled:bg-gray-100"
          />
        </div>
        <div className="flex items-center gap-1 text-sm">
          {([
            ['week', 'Diese Woche'],
            ['month', 'Dieser Monat'],
            ['year', 'Dieses Schuljahr'],
            ['all', 'Alle'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className="px-2.5 py-1 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              {label}
            </button>
          ))}
        </div>
        <select
          value={teacherId}
          onChange={e => { setTeacherId(e.target.value); setPage(0) }}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 min-w-44"
        >
          <option value="">Alle Lehrpersonen</option>
          {(teachersQuery.data?.teachers ?? []).map(t => (
            <option key={t.id} value={t.id}>
              {t.last_name} {t.first_name}
            </option>
          ))}
        </select>
        <input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Suche Fach / Klasse…"
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-48"
        />
        {hrOn && (
          <>
            <div className="flex items-center gap-1 text-sm">
              {(['unprocessed', 'settled', 'skipped'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => toggleStatusFilter(status)}
                  className={`px-2.5 py-1 rounded-full border ${
                    hrStatuses.includes(status)
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {HR_STATUS_LABELS[status]}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 text-sm">
              {(['paid_out', 'lesson_account', 'none'] as const).map(payout => (
                <button
                  key={payout}
                  onClick={() => togglePayoutFilter(payout)}
                  className={`px-2.5 py-1 rounded-full border ${
                    hrPayouts.includes(payout)
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {HR_PAYOUT_LABELS[payout]}
                </button>
              ))}
            </div>
          </>
        )}
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={e => { setUnreadOnly(e.target.checked); setPage(0) }}
          />
          Nur ungelesene Chats
        </label>
      </div>

      {/* Tabelle */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3 cursor-pointer select-none" onClick={() => { setSort(sort === 'date_asc' ? 'date_desc' : 'date_asc'); setPage(0) }}>
                Datum &amp; Zeit{sortIndicator('date_asc')}
              </th>
              <th className="px-4 py-3">Fach / Klasse</th>
              <th className="px-4 py-3">Anfragender Lehrer</th>
              <th className="px-4 py-3 cursor-pointer select-none" onClick={() => { setSort('teacher'); setPage(0) }}>
                Stellvertreter{sortIndicator('teacher')}
              </th>
              <th className="px-4 py-3">Lektion</th>
              {hrOn && (
                <>
                  <th className="px-4 py-3 cursor-pointer select-none" onClick={() => { setSort('hr_status'); setPage(0) }}>
                    HR-Status{sortIndicator('hr_status')}
                  </th>
                  <th className="px-4 py-3">HR-Entscheidung</th>
                </>
              )}
              <th className="px-4 py-3">Chat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {listQuery.isLoading && (
              <tr><td colSpan={hrOn ? 8 : 6} className="px-4 py-8 text-center text-gray-500">Laden…</td></tr>
            )}
            {!listQuery.isLoading && items.length === 0 && (
              <tr><td colSpan={hrOn ? 8 : 6} className="px-4 py-8 text-center text-gray-500">Keine abgehaltenen Stellvertretungen im gewählten Zeitraum.</td></tr>
            )}
            {items.map(entry => (
              <tr key={entry.lesson_request_id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{formatDate(entry.date)}</div>
                  <div className="text-xs text-gray-500">
                    {entry.time_slot_name || '—'}
                    {entry.start_time ? ` · ${formatTime(entry.start_time)}${entry.end_time ? `–${formatTime(entry.end_time)}` : ''}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>{entry.subject_name || '—'}</div>
                  <div className="text-xs text-gray-500">{entry.klass_name || ''}</div>
                </td>
                <td className="px-4 py-3">{entry.requester_name || '—'}</td>
                <td className="px-4 py-3">{entry.substitute_name || '—'}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{entry.lesson_index}/{entry.lesson_count}</td>
                {hrOn && (
                  <>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${HR_STATUS_BADGES[entry.hr_status]}`}>
                        {HR_STATUS_LABELS[entry.hr_status]}
                      </span>
                      {entry.hr_adjusted && (
                        <span className="ml-1 inline-flex px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">
                          Angepasst
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {entry.hr_status === 'unprocessed' ? (
                        <span className="text-gray-400 text-xs">Noch keine HR-Entscheidung</span>
                      ) : (
                        <div className="text-xs" title={entry.hr_note || undefined}>
                          <div>{entry.hr_payout ? HR_PAYOUT_LABELS[entry.hr_payout] : '—'}</div>
                          <div className="text-gray-500">
                            {entry.hr_cost_bearer || ''}
                            {entry.hr_amount != null ? ` · ${entry.hr_amount.toLocaleString('de-CH', { minimumFractionDigits: 2 })}` : ''}
                          </div>
                          {entry.hr_decided_at && (
                            <div className="text-gray-400">Entschieden am {formatDateTime(entry.hr_decided_at)}</div>
                          )}
                        </div>
                      )}
                    </td>
                  </>
                )}
                <td className="px-4 py-3">
                  <button
                    onClick={() => openChat(entry)}
                    className="relative flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-100"
                    title="Chat zur Anfrage öffnen"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Chat
                    {entry.chat_unread_count > 0 && (
                      <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-5 h-5 flex items-center justify-center px-1">
                        {entry.chat_unread_count > 99 ? '99+' : entry.chat_unread_count}
                      </span>
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <div>
          {total === 0 ? 'Keine Einträge' : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} von ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Zurück
          </button>
          <span>Seite {page + 1} von {pageCount}</span>
          <button
            onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
            disabled={page >= pageCount - 1}
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            Weiter <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
